import { Check, Lock } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { MarkKind } from '../manifest/manifestView';

/**
 * The four state tokens, drawn.
 *
 * Six marks, no others, and none of them is a colour on its own: the your-move dot is the only
 * brand fill in the column, waiting is a hollow amber ring, locked is a padlock, done is a
 * hairline check in muted (NEVER green — green fires exactly once per contract, on the
 * activation receipt), a DORMANT row gets a muted dot that carries no urgency at all, and a
 * refused row goes destructive.
 *
 * THE MUTED DOT IS NOT "EMPTY", IT IS "UNTOUCHABLE". An empty row the admin could fill in right
 * now — the premium, the loan, the driver file, and BOTH agreements' paper since 2026-08-04 —
 * wears the brand dot with everything else that is their move (`markFor`); the muted one is left
 * for a row with no form and no document behind it at all, and the manifest draws none today
 * (owner's ruling: both conditional agreements are counted and blue from the first draw).
 *
 * The mark is `aria-hidden`; the row prints the same fact as a word (`MARK_WORDS`) for screen
 * readers, because a 6px dot that means "somebody else is holding this up" has to survive not
 * being seen.
 */
const DOT_CLASS: Partial<Record<MarkKind, string>> = {
    move: 'sd-dot-move',
    wait: 'sd-dot-wait',
    draft: 'sd-dot-draft',
    record: 'sd-dot-record',
    refused: 'sd-dot-bad',
};

export default function StateMark({ kind }: { kind: MarkKind }) {
    if (kind === 'done') {
        return (
            <span className="sd-mark" aria-hidden="true">
                <Check className="h-3 w-3" strokeWidth={1.6} />
            </span>
        );
    }

    if (kind === 'locked') {
        return (
            <span className="sd-mark" aria-hidden="true">
                <Lock className="h-3 w-3" strokeWidth={1.5} />
            </span>
        );
    }

    return (
        <span className={cn('sd-mark', kind === 'refused' && 'sd-mark-bad')} aria-hidden="true">
            <span className={cn('sd-dot', DOT_CLASS[kind])} />
        </span>
    );
}
